// Rendered in place of an empty cell.
export const DASH = '—';

// Feature explainer shown in the "?" tooltip next to a row label: a bold heading + a
// plain-language description (rendered by FeatureTooltip → HoverCard.Title / .Description).
export type ComparisonRowTooltip = {
  descriptionKey: TranslationKey;
  titleKey: TranslationKey;
};

export type ComparisonRow = {
  // [Creator, Creator Pro, Business, Enterprise]
  values: [
    TranslationKey | typeof DASH,
    TranslationKey | typeof DASH,
    TranslationKey | typeof DASH,
    TranslationKey | typeof DASH,
  ];
  // A small badge after the label (e.g. "New" for a feature just added to the plans).
  badgeKey?: TranslationKey;
  // The minutes row is derived from live API data at render time; `values` acts as a fallback.
  isMinutesRow?: boolean;
  key: string;
  labelKey: TranslationKey;
  // Explanatory copy shown in the "?" tooltip next to the label (icon-only trigger).
  tooltip?: ComparisonRowTooltip;
};

// "Compare what changes when you scale" — rows/values mirror the Figma pricing design.
// Rows beyond COLLAPSED_ROW_COUNT are revealed by the "See all features" toggle.
export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    isMinutesRow: true,
    key: 'minutes',
    labelKey: 'pricing.compare.row.minutes',
    tooltip: {
      descriptionKey: 'pricing.compare.row.minutesDescription',
      titleKey: 'pricing.compare.row.minutesTitle',
    },
    values: [DASH, DASH, DASH, 'pricing.price.custom'],
  },
  {
    key: 'extraMinutes',
    // Visible only when the billing toggle is set to Annual (see EXTRA_MINUTES_ROW_KEY).
    labelKey: 'pricing.compare.extraMinutes',
    tooltip: {
      descriptionKey: 'pricing.compare.row.extraDescription',
      titleKey: 'pricing.compare.extraMinutes',
    },
    values: [
      'pricing.compare.value.upgradePro',
      'pricing.compare.value.extraMinute',
      'pricing.compare.value.extraMinute',
      'pricing.price.custom',
    ],
  },
  {
    key: 'savedVoices',
    labelKey: 'pricing.compare.row.voices',
    tooltip: {
      descriptionKey: 'pricing.compare.row.voicesDescription',
      titleKey: 'pricing.compare.row.voices',
    },
    values: [
      DASH,
      'pricing.compare.value.three',
      'pricing.compare.value.sharedVoices',
      'pricing.compare.value.voiceLibrary',
    ],
  },
  {
    key: 'lipSync',
    labelKey: 'pricing.compare.row.lipSync',
    tooltip: {
      descriptionKey: 'pricing.compare.row.lipSyncDescription',
      titleKey: 'pricing.compare.row.lipSync',
    },
    values: [
      DASH,
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
    ],
  },
  {
    key: 'controls',
    labelKey: 'pricing.compare.row.controls',
    tooltip: {
      descriptionKey: 'pricing.compare.row.controlsDescription',
      titleKey: 'pricing.compare.row.controls',
    },
    values: [
      DASH,
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
    ],
  },
  {
    key: 'glossary',
    labelKey: 'pricing.compare.row.glossary',
    tooltip: {
      descriptionKey: 'pricing.compare.row.glossaryDescription',
      titleKey: 'pricing.compare.row.glossary',
    },
    values: [DASH, DASH, 'pricing.compare.value.sharedGlossary', 'pricing.compare.value.managedTerms'],
  },
  {
    key: 'multiLanguage',
    labelKey: 'pricing.compare.row.multiLanguage',
    tooltip: {
      descriptionKey: 'pricing.compare.row.multiLanguageDescription',
      titleKey: 'pricing.compare.row.multiLanguage',
    },
    values: [DASH, DASH, 'pricing.compare.value.multiBatch', 'pricing.compare.value.customBatch'],
  },
  {
    badgeKey: 'pricing.compare.badge.new',
    key: 'onscreenTranslation',
    labelKey: 'pricing.compare.row.onscreen',
    tooltip: {
      descriptionKey: 'pricing.compare.row.onscreenDescription',
      titleKey: 'pricing.compare.row.onscreen',
    },
    // Enterprise gets it as a paid add-on through sales, as the paywall offers it (getOnscreenOffer → addOn).
    values: [DASH, DASH, 'pricing.compare.value.included', 'pricing.compare.value.addOn'],
  },
  {
    key: 'reviewWorkflow',
    labelKey: 'pricing.compare.row.review',
    tooltip: {
      descriptionKey: 'pricing.compare.row.reviewDescription',
      titleKey: 'pricing.compare.row.review',
    },
    values: [DASH, DASH, 'pricing.compare.value.reviewer', 'pricing.compare.value.managedQa'],
  },
  {
    key: 'teamWorkspace',
    labelKey: 'pricing.compare.row.team',
    tooltip: {
      descriptionKey: 'pricing.compare.row.teamDescription',
      titleKey: 'pricing.compare.row.team',
    },
    values: [
      DASH,
      'pricing.compare.value.threeMembers',
      'pricing.compare.value.fiveMembers',
      'pricing.compare.value.customRoles',
    ],
  },
  {
    key: 'prompting',
    labelKey: 'pricing.compare.row.prompting',
    tooltip: {
      descriptionKey: 'pricing.compare.row.promptingDescription',
      titleKey: 'pricing.compare.row.prompting',
    },
    values: [DASH, DASH, DASH, 'pricing.compare.value.included'],
  },
  // --- revealed by "See all features" ---
  {
    key: 'processingSpeed',
    labelKey: 'pricing.compare.row.speed',
    tooltip: {
      descriptionKey: 'pricing.compare.row.speedDescription',
      titleKey: 'pricing.compare.row.speed',
    },
    values: [
      'pricing.compare.value.standardQueue',
      'pricing.compare.value.standardQueue',
      'pricing.compare.value.priorityQueue',
      'pricing.compare.value.dedicated',
    ],
  },
  {
    key: 'api',
    labelKey: 'pricing.compare.row.api',
    tooltip: {
      descriptionKey: 'pricing.compare.row.apiDescription',
      titleKey: 'pricing.compare.row.api',
    },
    values: [
      DASH,
      'pricing.compare.value.sandboxApi',
      'pricing.compare.value.fullApi',
      'pricing.compare.value.customIntegrations',
    ],
  },
  {
    key: 'security',
    labelKey: 'pricing.compare.row.security',
    tooltip: {
      descriptionKey: 'pricing.compare.row.securityDescription',
      titleKey: 'pricing.compare.row.security',
    },
    values: [DASH, DASH, 'pricing.compare.value.standard', 'pricing.compare.value.compliance'],
  },
  {
    key: 'srt',
    labelKey: 'pricing.compare.row.srt',
    tooltip: {
      descriptionKey: 'pricing.compare.row.srtDescription',
      titleKey: 'pricing.compare.row.srtTitle',
    },
    values: [
      DASH,
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
    ],
  },
  {
    key: 'screenRecorder',
    labelKey: 'pricing.compare.row.screenRecorder',
    tooltip: {
      descriptionKey: 'pricing.compare.row.screenRecorderDescription',
      titleKey: 'pricing.compare.row.screenRecorder',
    },
    values: [
      'pricing.compare.value.unlimited',
      'pricing.compare.value.unlimited',
      'pricing.compare.value.unlimited',
      'pricing.compare.value.unlimited',
    ],
  },
  {
    key: 'autoZoom',
    labelKey: 'pricing.compare.row.autoZoom',
    tooltip: {
      descriptionKey: 'pricing.compare.row.autoZoomDescription',
      titleKey: 'pricing.compare.row.autoZoom',
    },
    values: [
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
    ],
  },
  {
    key: 'autoCut',
    labelKey: 'pricing.compare.row.autoCut',
    tooltip: {
      descriptionKey: 'pricing.compare.row.autoCutDescription',
      titleKey: 'pricing.compare.row.autoCut',
    },
    values: [
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
      'pricing.compare.value.included',
    ],
  },
];

// The "Extra minutes" row only applies to annual plans → hidden when the toggle is Monthly.
export const EXTRA_MINUTES_ROW_KEY = 'extraMinutes';

// Rows visible before the user expands "See all features".
export const COLLAPSED_ROW_COUNT = 11; // through Prompting, On-screen text translation included

export const getVisibleComparisonRows = ({
  showAll,
  yearlySelected,
}: {
  showAll: boolean;
  yearlySelected: boolean;
}) => {
  const activeRows = yearlySelected
    ? COMPARISON_ROWS
    : COMPARISON_ROWS.filter((row) => row.key !== EXTRA_MINUTES_ROW_KEY);

  if (showAll) return activeRows;

  const collapsedRowKeys = new Set(COMPARISON_ROWS.slice(0, COLLAPSED_ROW_COUNT).map((row) => row.key));
  return activeRows.filter((row) => collapsedRowKeys.has(row.key));
};
import { TranslationKey } from '@/shared/i18n';
